import React, { useState } from 'react';
import {
  Button,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { X } from 'lucide-react';
import type {
  TrainingVisitKind,
  TrainingVisitStatus,
} from '../../services/api/types.gen';
import {
  CONTENT_LIMITS,
  DEMO_CONTENT_DEFAULTS,
  DISCIPLINE_VISIT_KINDS,
  FILL_STEPS,
  isOasisVisit,
  newVisit,
  type ResolvedDemoContent,
  type ResolvedVisit,
  VISIT_KIND_LABELS,
  VISIT_STATUS_LABELS,
} from './demoContent';
import { DemoFormSection } from './DemoFormSection';

interface Props {
  content: ResolvedDemoContent;
  includeCoding: boolean;
  /**
   * The caller's own sections, as the last cells of the sections grid. A list
   * rather than a slot per box, so adding one is a change in the caller.
   */
  trailing?: React.ReactNode;
  onChange: (content: ResolvedDemoContent, includeCoding: boolean) => void;
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

/**
 * A small whole-number input held to [min, max]. What is typed is kept as typed
 * while the box has focus, and clamped when it loses it, so clearing it to
 * type a new number does not jump to the minimum first.
 */
const NumberBox: React.FC<{
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  ariaLabel?: string;
}> = ({ value, min, max, onChange, ariaLabel }) => {
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <Input
      inputSize='sm'
      type='number'
      min={min}
      max={max}
      aria-label={ariaLabel}
      value={draft ?? value}
      onChange={event => {
        setDraft(event.target.value);
        const parsed = Number.parseInt(event.target.value, 10);
        if (!Number.isNaN(parsed) && parsed >= min && parsed <= max)
          onChange(parsed);
      }}
      onBlur={() => {
        if (draft !== null) {
          const parsed = Number.parseInt(draft, 10);
          onChange(clamp(Number.isNaN(parsed) ? value : parsed, min, max));
        }
        setDraft(null);
      }}
    />
  );
};

const VISIT_COLUMNS =
  'md:grid-cols-[minmax(0,1.4fr)_5rem_minmax(0,1fr)_minmax(0,1fr)_1.5rem]';

/** The training patient's visits, one row each; the SOC row is fixed first. */
const VisitRows: React.FC<{
  visits: ResolvedVisit[];
  onChange: (visits: ResolvedVisit[]) => void;
}> = ({ visits, onChange }) => {
  const update = (index: number, patch: Partial<ResolvedVisit>) =>
    onChange(
      visits.map((row, i) => (i === index ? { ...row, ...patch } : row))
    );
  const hasRecert = visits.some(row => row.kind === 'recert');
  const hasDischarge = visits.some(row => row.kind === 'discharge');
  return (
    // No border and no card around the rows: this table already sits inside a
    // section with both, and a box in a box made the visits look like a
    // separate thing rather than the section's content.
    <div className='space-y-2'>
      <div>
        <div
          aria-hidden='true'
          className={`hidden gap-2 px-1 pb-1 text-xs font-medium text-muted-foreground md:grid ${VISIT_COLUMNS}`}
        >
          <span>Visit</span>
          <span>Day</span>
          <span>Status</span>
          <span>Progress</span>
          <span />
        </div>
        {/* No rules between rows: every cell is already a bordered control,
            so the lines drew a grid on top of a grid. */}
        <ul>
          {visits.map((row, index) => {
            const name = `${VISIT_KIND_LABELS[row.kind]} visit on day ${row.day}`;
            const isSoc = index === 0;
            return (
              <li
                key={index}
                className={`grid grid-cols-2 items-center gap-2 px-1 py-1 ${VISIT_COLUMNS}`}
              >
                {/* The two fixed cells read like the values beside them --
                    same size and weight as the SOC's own "All answered" -- so
                    the row is one line rather than two kinds of text. */}
                {isSoc ? (
                  <span className='text-xs text-muted-foreground'>SOC</span>
                ) : (
                  <Select
                    value={row.kind}
                    onValueChange={kind =>
                      update(index, {
                        kind: kind as TrainingVisitKind,
                        // A discharge has fewer gaps than a SOC or recert.
                        ...(kind === 'discharge' && {
                          scrubber_errors: Math.min(
                            row.scrubber_errors,
                            CONTENT_LIMITS.dischargeScrubberErrors
                          ),
                        }),
                      })
                    }
                  >
                    <SelectTrigger
                      selectSize='sm'
                      aria-label={`Type of ${name}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='skilled_nursing'>
                        Skilled nursing
                      </SelectItem>
                      <SelectItem
                        value='recert'
                        disabled={hasRecert && row.kind !== 'recert'}
                      >
                        Recert
                      </SelectItem>
                      {DISCIPLINE_VISIT_KINDS.map(kind => (
                        <SelectItem key={kind} value={kind}>
                          {VISIT_KIND_LABELS[kind]}
                        </SelectItem>
                      ))}
                      <SelectItem
                        value='discharge'
                        disabled={hasDischarge && row.kind !== 'discharge'}
                      >
                        Discharge
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
                {isSoc ? (
                  <span className='text-xs text-muted-foreground'>0</span>
                ) : (
                  <NumberBox
                    ariaLabel={`Day of ${name}`}
                    value={row.day}
                    min={1}
                    max={CONTENT_LIMITS.lastEpisodeDay}
                    onChange={day => update(index, { day })}
                  />
                )}
                <Select
                  value={row.status}
                  onValueChange={status =>
                    update(index, { status: status as TrainingVisitStatus })
                  }
                >
                  <SelectTrigger
                    selectSize='sm'
                    aria-label={`Status of ${name}`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(VISIT_STATUS_LABELS).map(
                      ([value, label]) => (
                        <SelectItem
                          key={value}
                          value={value}
                          disabled={
                            value !== 'not_started' &&
                            row.day > CONTENT_LIMITS.todayEpisodeDay
                          }
                        >
                          {label}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
                {row.status !== 'in_progress' ? (
                  <span className='text-xs text-muted-foreground'>
                    {row.status === 'completed' ? 'All answered' : 'Empty'}
                  </span>
                ) : isOasisVisit(row.kind) ? (
                  <div className='flex items-center gap-1.5'>
                    <NumberBox
                      ariaLabel={`Scrubber errors on ${name}`}
                      value={row.scrubber_errors}
                      min={1}
                      max={
                        row.kind === 'discharge'
                          ? CONTENT_LIMITS.dischargeScrubberErrors
                          : CONTENT_LIMITS.socScrubberErrors
                      }
                      onChange={scrubber_errors =>
                        update(index, { scrubber_errors })
                      }
                    />
                    <span className='shrink-0 text-xs text-muted-foreground'>
                      errors
                    </span>
                  </div>
                ) : (
                  <Select
                    value={String(row.filled_percent)}
                    onValueChange={value =>
                      update(index, { filled_percent: Number(value) })
                    }
                  >
                    <SelectTrigger
                      selectSize='sm'
                      aria-label={`Progress of ${name}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FILL_STEPS.map(step => (
                        <SelectItem key={step} value={String(step)}>
                          {step}% filled
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {isSoc ? (
                  <span />
                ) : (
                  <button
                    type='button'
                    aria-label={`Remove ${name}`}
                    onClick={() =>
                      onChange(visits.filter((_, i) => i !== index))
                    }
                    className='inline-flex justify-self-end rounded text-muted-foreground hover:text-foreground'
                  >
                    <X className='h-4 w-4' />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

const SectionToggle: React.FC<{
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}> = ({ label, checked, onChange }) => (
  <label className='flex items-center gap-2'>
    <Checkbox
      aria-label={label}
      checked={checked}
      onCheckedChange={value => onChange(value === true)}
    />
    <span className='text-xs'>On</span>
  </label>
);

/** A section's fields, greyed and inert while the section is off. */
const SectionBody: React.FC<{
  enabled: boolean;
  children: React.ReactNode;
}> = ({ enabled, children }) => (
  <fieldset
    disabled={!enabled}
    className={`space-y-2 ${enabled ? '' : 'opacity-50'}`}
  >
    {children}
  </fieldset>
);

/** What sample data each step of a build seeds. */
// Memoized: the dialog keeps this tab mounted while hidden, so without it every
// keystroke in another tab re-renders these selects.
export const DemoContentTab = React.memo(function DemoContentTab({
  content,
  includeCoding,
  trailing,
  onChange,
}: Props) {
  const patient = content.training_patient;
  const set = <K extends keyof ResolvedDemoContent>(
    section: K,
    patch: Partial<ResolvedDemoContent[K]>
  ) =>
    onChange(
      { ...content, [section]: { ...content[section], ...patch } },
      includeCoding
    );

  return (
    <div className='space-y-3'>
      <DemoFormSection
        title='Visits'
        note="on John Roger's chart"
        actions={
          <>
            {/* Back to the chart every demo had, so an edited list is not a
                one-way door. Saving is still the Save button's job. */}
            <Button
              type='button'
              size='sm'
              variant='outline'
              disabled={!patient.enabled}
              onClick={() =>
                set('training_patient', {
                  visits: DEMO_CONTENT_DEFAULTS.training_patient.visits,
                })
              }
            >
              Reset to default
            </Button>
            <SectionToggle
              label='Visits on'
              checked={patient.enabled}
              onChange={enabled => set('training_patient', { enabled })}
            />
          </>
        }
      >
        <SectionBody enabled={patient.enabled}>
          <VisitRows
            visits={patient.visits}
            onChange={visits => set('training_patient', { visits })}
          />
          {/* The standing fact and the one action share a line, so the table
              is not followed by two short rows of its own. */}
          <div className='flex flex-wrap items-center justify-between gap-2 pt-1'>
            <p className='text-xs text-muted-foreground'>
              Plus a wound with photos, medications, and long- and short-term
              goals.
            </p>
            <Button
              type='button'
              size='sm'
              variant='outline'
              disabled={patient.visits.length >= CONTENT_LIMITS.visits}
              onClick={() =>
                set('training_patient', {
                  visits: [...patient.visits, newVisit(patient.visits)],
                })
              }
            >
              Add visit
            </Button>
          </div>
        </SectionBody>
      </DemoFormSection>

      <div className='grid gap-3 lg:grid-cols-2'>
        <DemoFormSection
          title='Intakes'
          actions={
            <SectionToggle
              label='Intakes on'
              checked={content.intakes.enabled}
              onChange={enabled => set('intakes', { enabled })}
            />
          }
        >
          <SectionBody enabled={content.intakes.enabled}>
            <p className='text-xs text-muted-foreground'>
              One intake in each status.
            </p>
          </SectionBody>
        </DemoFormSection>

        <DemoFormSection
          title='Billing'
          actions={
            <SectionToggle
              label='Billing on'
              checked={content.billing.enabled}
              onChange={enabled => set('billing', { enabled })}
            />
          }
        >
          <SectionBody enabled={content.billing.enabled}>
            <p className='text-xs text-muted-foreground'>
              A patient in every claim state, and three denied.
            </p>
          </SectionBody>
        </DemoFormSection>

        <DemoFormSection
          title='Reporting'
          actions={
            <SectionToggle
              label='Reporting on'
              checked={content.reporting.enabled}
              onChange={enabled => set('reporting', { enabled })}
            />
          }
        >
          <SectionBody enabled={content.reporting.enabled}>
            <p className='text-xs text-muted-foreground'>
              50 patients, with the episodes, visits and claims behind every
              report.
            </p>
          </SectionBody>
        </DemoFormSection>

        {/* Its own box rather than part of Reporting: the generator refuses an
            agency whose clinics have no SHP provider number, so a sandbox that
            cannot have one says so instead of reporting a skip every run. */}
        <DemoFormSection
          title='CAHPS'
          actions={
            <SectionToggle
              label='CAHPS on'
              checked={content.cahps.enabled}
              onChange={enabled => set('cahps', { enabled })}
            />
          }
        >
          <SectionBody enabled={content.cahps.enabled}>
            <p className='text-xs text-muted-foreground'>One CAHPS export.</p>
          </SectionBody>
        </DemoFormSection>

        <DemoFormSection
          title='Coding'
          actions={
            <SectionToggle
              label='Coding on'
              checked={includeCoding}
              onChange={enabled => onChange(content, enabled)}
            />
          }
        >
          <SectionBody enabled={includeCoding}>
            <p className='text-xs text-muted-foreground'>
              Three charts with completed demo diagnoses.
            </p>
          </SectionBody>
        </DemoFormSection>

        {/* The page's own boxes, in this grid so they pair with the content
            sections rather than starting a row of their own below. */}
        {trailing}
      </div>
    </div>
  );
});
